/**
 * Returns the first route the current user has access to,
 * based on features + permissions stored in sessionStorage.
 * Used after login to redirect to the right landing page.
 */
export function getFirstAccessibleRoute() {
    // Engage tenants land on the Engage command center regardless of TrustEdge permissions.
    const productType = sessionStorage.getItem("product_type");
    if (productType === "engage") return "/engage";

    const role = sessionStorage.getItem("login_role");
    // Only super_admin bypasses page permissions. 'admin' was included here as
    // well, which sent every admin to /dashboard — a route the guard then
    // refused, bouncing straight back here and landing them on an empty page.
    // Must track FULL_ACCESS_ROLE_TYPES in platform_core/authentication/utils.py.
    const isSuperAdmin = role === "super_admin";
    // Admins always administer people, whatever their assigned role grants.
    const isPeopleAdmin = isSuperAdmin || role === "admin";

    let features = {};
    let permissions = {};
    try { features    = JSON.parse(sessionStorage.getItem("features")    || "{}"); } catch {}
    try { permissions = JSON.parse(sessionStorage.getItem("permissions") || "{}"); } catch {}

    const canView = (module) => {
        if (isSuperAdmin) return true;
        return features[module] && permissions[module]?.view;
    };

    // Engage tenants — land on the first Engage page the role can access.
    if (sessionStorage.getItem("product_type") === "engage") {
        const engagePages = [
            ["engage_dashboard",          "/engage/dashboard"],
            ["engage_enquiries",          "/engage/enquiries"],
            ["engage_referring_doctors",  "/engage/referring-doctors"],
            ["engage_ambulance",          "/engage/ambulance-providers"],
            ["engage_corporate_accounts", "/engage/corporate-accounts"],
            ["engage_digital_marketing",  "/engage/digital-marketing"],
            ["engage_followups",          "/engage/followups"],
            ["engage_bulk_import",        "/engage/imports"],
            ["engage_reports",            "/engage/reports"],
            ["engage_ai_caller",          "/engage/ai-caller/bucket"],
        ];
        for (const [mod, route] of engagePages) {
            if (canView(mod)) return route;
        }
        if (isPeopleAdmin || permissions["users"]?.view)    return "/engage/settings/users";
        if (isSuperAdmin || permissions["settings"]?.view) return "/engage/settings/sources";
        return "/engage/no-access";   // nothing granted — terminal page, no loop
    }

    // Every TrustEdge login lands here, and so does every bounce from a page the
    // role cannot open. The route is ungated and its numbers are scoped to the
    // caller, so there is always something true to show and never a redirect loop
    // — the long fallback chain this replaced existed only because /dashboard
    // could refuse the user and send them straight back here.
    return "/dashboard";
}
